const CHATBOT_SETTINGS_STORAGE_KEY = 'nomination_chatbot_settings';

export const CHATBOT_FONT_FAMILY_OPTIONS = [
  {
    value: 'DM Sans',
    name: 'DM Sans',
    css: "'DM Sans', sans-serif",
  },
  {
    value: 'Georgia',
    name: 'Georgia',
    css: 'Georgia, serif',
  },
  {
    value: 'system',
    name: 'System',
    css: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    value: 'monospace',
    name: 'Monospace',
    css: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
];

export const CHATBOT_FONT_SIZE_OPTIONS = [
  {
    value: 13,
    name: 'S',
  },
  {
    value: 15,
    name: 'M',
  },
  {
    value: 17,
    name: 'L',
  },
];

export const DEFAULT_CHATBOT_SETTINGS = {
  font_family: 'DM Sans',
  font_size: 15,
  auto_scroll: true,
  render_markdown: true,
  show_detailed_loading_steps: true,
  raw_workflow_steps: false,
  show_follow_up_suggestions: false,
  show_sources: false,
  api_key: '',
  drag_offset_x: 0,
};

const isValidFontFamily = (value) => (
  CHATBOT_FONT_FAMILY_OPTIONS.some((option) => option.value === value)
);

const isValidFontSize = (value) => (
  CHATBOT_FONT_SIZE_OPTIONS.some((option) => option.value === value)
);

const normalizeDragOffsetX = (value) => {
  const offset = Number(value);

  if (!Number.isFinite(offset)) {
    return DEFAULT_CHATBOT_SETTINGS.drag_offset_x;
  }

  return offset;
};

export const getChatbotCustomApiKey = (settings = {}) => {
  if (typeof settings.api_key !== 'string') {
    return '';
  }

  return settings.api_key.trim();
};

export const normalizeChatbotSettings = (settings = {}) => {
  const font_family = isValidFontFamily(settings.font_family)
    ? settings.font_family
    : DEFAULT_CHATBOT_SETTINGS.font_family;
  const font_size = isValidFontSize(Number(settings.font_size))
    ? Number(settings.font_size)
    : DEFAULT_CHATBOT_SETTINGS.font_size;

  return {
    font_family,
    font_size,
    auto_scroll: settings.auto_scroll !== false,
    render_markdown: settings.render_markdown !== false,
    show_detailed_loading_steps: settings.show_detailed_loading_steps !== false,
    raw_workflow_steps: Boolean(settings.raw_workflow_steps),
    show_follow_up_suggestions: Boolean(settings.show_follow_up_suggestions),
    show_sources: Boolean(settings.show_sources),
    api_key: getChatbotCustomApiKey(settings),
    drag_offset_x: normalizeDragOffsetX(settings.drag_offset_x),
  };
};

export const loadChatbotSettings = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return { ...DEFAULT_CHATBOT_SETTINGS };
    }

    const raw = window.localStorage.getItem(CHATBOT_SETTINGS_STORAGE_KEY);

    if (!raw) {
      return { ...DEFAULT_CHATBOT_SETTINGS };
    }

    return normalizeChatbotSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CHATBOT_SETTINGS };
  }
};

export const saveChatbotSettings = (settings) => {
  const normalized = normalizeChatbotSettings(settings);

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        CHATBOT_SETTINGS_STORAGE_KEY,
        JSON.stringify(normalized)
      );
    }
  } catch {
    // Ignore quota / private-mode failures
  }

  return normalized;
};

export const getChatbotFontFamilyCss = (font_family) => {
  const option = CHATBOT_FONT_FAMILY_OPTIONS.find(
    (item) => item.value === font_family
  );

  return option?.css || CHATBOT_FONT_FAMILY_OPTIONS[0].css;
};
